import { type WorkerAvailabilityCalendarConfig } from "../WorkerAvailability/types";
import { type CbhFeatureFlag } from "./CbhFeatureFlag";
import { type DynamicPenaltiesConfig } from "./CbhFeatureFlags";

export enum EnableReferralSignupScreenVariants {
  OFF = "off",
  ON_WITH_CONTACTS = "on-with-contacts",
  ON_WITHOUT_CONTACTS = "on-without-contacts",
}

type MSABonusConfig = Record<string, Record<string, number>>;

interface DefaultBonusConfig {
  default: Record<string, number>;
  defaultReferralRate: number;
}

export type SignupBasedReferralsBonusConfig = DefaultBonusConfig | MSABonusConfig;

type HcpLicenseFieldsRequirementsVariant = Record<
  string,
  Record<string, "hidden" | "visible" | "allowed">
>;

type HcpLicenseDefaultStatusVariant = Record<
  string,
  {
    status: "Active" | "Pending";
  }
>;

interface NlcStatesVariant {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  active_states: string[];
}

interface PersonalIDSubtypesVariant {
  manual: string[];
  stripe: string[];
}

interface RateNegotiationActivationVariant {
  id: string[];
  state: string[];
  msa: string[];
  global: boolean;
}

interface RateNegotiationLimitsVariant {
  [k: string]: number;
  Maximum: number;
  Minimum: number;
}

interface ToastOptions {
  header: string;
  message: string;
}

interface SentHomeRequestConfigVariant {
  failureToast: ToastOptions;
  successToast: ToastOptions;
  confirmationAlert: ToastOptions;
  isSentHomeFlowV2Enabled: boolean;
}

interface WorkWithFriendsBonusesVariant {
  addFriendBonus?: number;
  groupAttendanceBonus?: number;
}

interface OpenShiftCardAgentRequestVariant {
  agentReq?: Record<string, boolean>;
  global: true;
}

interface SendFCFToUrgentShiftsFlowVariant {
  timeWindow: number;
  enabled: boolean;
}

export interface MaxReferralBonusConfig {
  onboardingBonusInDollars?: number;
  postOnboardingBonusInDollars?: number;
}

export type WorkerToWorkerReferralType = "FLEX_ACTIVATION" | "FLEX_ACTIVATION_OR_HIRING";

export interface FeatureFlagVariants {
  [CbhFeatureFlag.ENABLE_COGNITO_AUTH_SYNC]: boolean;
  [CbhFeatureFlag.ENABLE_COGNITO_ONBOARDING]: boolean;
  [CbhFeatureFlag.FACILITY_TYPE_SHIFT_ICONS]: Record<string, boolean>;
  [CbhFeatureFlag.HCP_APP_RATING_PROMPT_INTERVAL]: number;
  [CbhFeatureFlag.HCP_LICENSE_FIELDS_REQUIREMENTS]: HcpLicenseFieldsRequirementsVariant;
  [CbhFeatureFlag.HCP_LICENSE_DEFAULT_STATUS]: HcpLicenseDefaultStatusVariant;
  [CbhFeatureFlag.LICENSE_RECIPROCITY]: boolean;
  [CbhFeatureFlag.NLC_STATES]: NlcStatesVariant;
  [CbhFeatureFlag.ONBOARDING_STRIPE_SETUP_UNSKIPPABLE]: boolean;
  [CbhFeatureFlag.PERSONAL_ID_SUBTYPES]: PersonalIDSubtypesVariant;

  [CbhFeatureFlag.RATE_NEGOTIATION_ACTIVATION]: RateNegotiationActivationVariant;
  [CbhFeatureFlag.RATE_NEGOTIATION_LEAD_TIME]: number;
  [CbhFeatureFlag.RATE_NEGOTIATION_LIMITS]: RateNegotiationLimitsVariant;
  [CbhFeatureFlag.READS_FILE_STORAGE_MOBILE]: boolean;
  [CbhFeatureFlag.SENDBIRD_CONCURRENT_CONNECTION_OPTIMISATION]: boolean;
  [CbhFeatureFlag.SENT_HOME_REQUEST_CONFIG]: SentHomeRequestConfigVariant;
  [CbhFeatureFlag.SHIFTS_SORT_KEY_TIMESLOT]: "pay" | "distance";
  [CbhFeatureFlag.SHOW_CANCELLATION_PAYOUT]: boolean;
  [CbhFeatureFlag.TIMESHEETS_V2_NON_IP]: boolean;
  [CbhFeatureFlag.URGENT_SHIFTS]: boolean;
  [CbhFeatureFlag.USE_ORIGINALAMOUNT_FIELD_NON_IP]: boolean;
  [CbhFeatureFlag.WORK_WITH_FRIENDS_BONUSES]: WorkWithFriendsBonusesVariant;
  [CbhFeatureFlag.OPEN_SHIFT_CARD_AGENT_REQ]: OpenShiftCardAgentRequestVariant;
  [CbhFeatureFlag.SEND_FCF_TO_URGENT_SHIFTS_FLOW]: SendFCFToUrgentShiftsFlowVariant;
  [CbhFeatureFlag.SIGNUP_BASED_REFERRAL]: SignupBasedReferralsBonusConfig;
  [CbhFeatureFlag.ENABLE_REFERRAL_SIGNUP_SCREEN]: EnableReferralSignupScreenVariants;
  [CbhFeatureFlag.ENABLE_BROWSING_SHIFTS_ACROSS_US_MAP_VIEW]: boolean;
  [CbhFeatureFlag.FACILITY_DISTANCE_THRESHOLD_MAP_VIEW_CONFIG]: Record<string, number>;
  [CbhFeatureFlag.SIGN_UP_REFERRAL_SCREEN_EXPIRATION_DAY]: number;
  [CbhFeatureFlag.SHOW_ONBOARDING_AGENT_PROFILE_ERROR_MODAL]: boolean;
  [CbhFeatureFlag.MAX_REFERRAL_BONUS_CONFIG]: MaxReferralBonusConfig;
  [CbhFeatureFlag.HCP_CAN_EDIT_DATE_FOR_MULTI_DAY_SHIFTS]: boolean;
  [CbhFeatureFlag.SHOW_SHIFT_STATUS_FOOTER_INFO]: boolean;
  [CbhFeatureFlag.URGENT_SHIFTS_CONFIG]: {
    /**
     * @deprecated FIXME: Should use services URL environment not from LD.
     * All it's usages must read from env.
     */
    urgentShiftsServiceUrl: string;
    commuteUrgentShiftBooking?: { subTitle?: string; bodyText?: string };
    lateUrgentShiftBooking?: { subTitle?: string; bodyText?: string };
    checkAttendanceBoundsInMinutes?: number[];
    urgentShiftPageQueryErrorMessage?: string;
    infoItems: string[];
    noLocationBookingAlertMessage: { android: string; ios: string };
  };
  [CbhFeatureFlag.ENABLE_WORKER_AVAILABILITY]: {
    qualifications: string[];
  };
  [CbhFeatureFlag.WORKER_AVAILABILITY_CALENDER_CONFIG]: WorkerAvailabilityCalendarConfig;
  [CbhFeatureFlag.URGENT_SHIFTS_HOURLY_PAY_RATE]: boolean;
  [CbhFeatureFlag.HCP_LICENSE_NUMBER_REQUIREMENTS]: Record<string, string[]>;
  [CbhFeatureFlag.WORKPLACE_REFERRALS]: {
    workerToWorkplace?: boolean;
    workerToWorkplaceIncentive?: Record<string, number>;
  };
  [CbhFeatureFlag.ENABLED_REFERRAL_PROGRAMS]: {
    activationReferralIsEnabled: boolean;
    signupReferralIsEnabled: boolean;
  };
  [CbhFeatureFlag.WORKER_TO_WORKPLACE_AFFILIATE_EXPERIMENT]: boolean;

  [CbhFeatureFlag.HOME_HEALTH_BOOK_DIALOG_ATTENDANCE_ENABLED]: boolean;
  [CbhFeatureFlag.HOME_HEALTH_VISIT_REQUIREMENT_CHECK]: boolean;
  [CbhFeatureFlag.WORK_WITH_FRIENDS_V2]: boolean;
  [CbhFeatureFlag.MISSED_SHIFTS_CONFIGURATION]: {
    enabled: boolean;
    maxResultsToShow?: number | undefined;
    minimumPayPercentile?: number | undefined;
  };
  [CbhFeatureFlag.DYNAMIC_PENALTIES_CONFIG]: DynamicPenaltiesConfig;
  [CbhFeatureFlag.CANCELLATION_STATS]: boolean;
  [CbhFeatureFlag.DOCUMENT_EXCLUSION_LIST]: {
    ids: string[];
  };
  [CbhFeatureFlag.WORKER_TO_WORKER_REFERRAL_TYPE]: { type: WorkerToWorkerReferralType };
  [CbhFeatureFlag.CONTRACTOR_AGREEMENT_RE_SIGNING]: {
    enabled: boolean;
    cutoffTimestamp: string; // ISO date string
  };
  [CbhFeatureFlag.FORTUNE_COOKIE_ANIMATION]: {
    enabled: boolean;
    fortuneMessages: string[];
  };
  [CbhFeatureFlag.PLACEMENT_BOOKING_FLOW_ORDER]: "book_first" | "screen_first" | "screen_last";
}
